import {Inject, Injectable, computed, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ServerEndpoints} from '../server.endpoints';

/** `GET /miscellaneous/Support/GetSupportContact` (#613) — `{}` (the field omitted) when no address is set. */
export interface SupportContactModel {
  email?: string;
}

/**
 * The support e-mail shown to a non-owner on the legal pages ("der Support übernimmt Änderungen für Sie") —
 * `CHEQME_EMAIL_SUPPORT` on the server, read once per session (#613).
 *
 * **Loads once, lazily, and is shared app-wide.** `providedIn: 'root'` makes this ONE instance for the whole
 * session, so the two legal pages (and anything else that needs the address later) share a single request
 * rather than each re-fetching it; the request fires the first time anything reads {@link email} or
 * {@link loaded}, not at app start, since most sessions (every PlatformOwner) never need it at all.
 *
 * Deliberately fails soft: a load error leaves {@link email} `undefined` rather than surfacing a banner — the
 * hint that reads it already renders gracefully with no address (see `legal-readonly-hint.component.ts`), and
 * a missing support contact is not something a ShardAdmin can act on.
 *
 * **That soft failure is also how the #769 defect stayed invisible for as long as it did.** The endpoint used
 * to be `GET /administration/Settings/GetSupportContact`, behind the administration surface's `MetaRole.Admin`
 * gate — so the company's `Steuerberater`, who reads the same TSE page, was answered 403, this `error` branch
 * swallowed it, and the sentence simply ended without an address. The endpoint now has a home of its own with
 * a plain `[Authorize]` (`API/Controller/Miscellaneous/SupportController.cs`), reachable by any signed-in
 * session. The fail-soft stays: a host with no mailing configured is still a legitimate answer of `{}`.
 */
@Injectable({providedIn: 'root'})
export class SupportContactService {
  private readonly state = signal<SupportContactModel | undefined>(undefined);
  private requested = false;

  /** The configured address, or `undefined` before the load resolves / when none is configured. */
  readonly email = computed(() => this.state()?.email);

  /** Whether the (possibly empty) response has arrived — lets a template distinguish "loading" from "no address". */
  readonly loaded = computed(() => this.state() !== undefined);

  constructor(@Inject('BASE_URL') private readonly baseUrl: string, private readonly httpClient: HttpClient) {
  }

  /** Triggers the one-time load if it has not started yet. Safe to call from every consumer's `ngOnInit`. */
  ensureLoaded(): void {
    if (this.requested) return;
    this.requested = true;

    this.httpClient.get<SupportContactModel>(`${this.baseUrl}${ServerEndpoints.Miscellaneous_Support_GetSupportContact}`)
      .subscribe({
        next: (response) => this.state.set(response ?? {}),
        // Fail soft — see the class doc. `{}` still marks `loaded() === true` (no infinite retry loop).
        error: () => this.state.set({})
      });
  }
}
